/**
 * Batch-6 verlof — pure berekeningen voor ouderschapsverlof (Wet betaald
 * ouderschapsverlof). Recht is PER KIND; alle parameters komen uit het
 * `VerlofBeleid` (geen wettelijke/CAO-waarde hardcoded in deze functies).
 *
 * Formule (spec Deel 2b):
 *   totaal    = ouderschapTotaalWeken × contracturenPerWeek
 *   betaald   = ouderschapBetaaldWeken × contracturenPerWeek
 *   onbetaald = totaal − betaald
 *   minus reedsOpgenomenVorigeWerkgeverUren (eerst van betaald wanneer
 *   betaaldDeelGebruikt, anders van onbetaald; een overschot loopt door naar
 *   het andere deel zodat het totaal nooit dubbel telt)
 *   vervaldata: betaald = geboortedatum + betaaldBinnenMaandenNaGeboorte;
 *               totaal  = de dag vóór de (instelbare, default 8e) verjaardag
 *
 * Voorbeelden (testcases): 38u/week → 988 totaal / 342 betaald / 646
 * onbetaald; 28u/week → 728 / 252 / 476.
 *
 * Datums zijn UTC-middernacht van de NL-kalenderdag (repo-conventie; zie
 * contract-uren.ts) — alle datum-rekenkunde hier gaat via de getUTC/setUTC-
 * varianten.
 */

import { round2 } from './leave-calculations'

export interface OuderschapsBeleidInput {
  /** Totaal recht in weken (wettelijk NL 2026: 26). */
  totaalWeken: number
  /** Betaald (UWV/WAZO-)deel in weken (wettelijk NL 2026: 9). */
  betaaldWeken: number
  /** Recht geldt tot deze verjaardag van het kind (wettelijk: 8). */
  kindTotLeeftijd: number
  /** Betaald deel opnemen binnen X maanden na geboorte (wettelijk: 12). */
  betaaldBinnenMaandenNaGeboorte: number
}

export interface OuderschapsKindInput {
  geboortedatum: Date
  /**
   * True wanneer het betaalde deel (deels) bij een vorige werkgever is
   * gebruikt — de verrekening gaat dan eerst van het betaalde deel af.
   */
  betaaldDeelGebruikt: boolean
  /** Bij een vorige werkgever reeds opgenomen uren (verrekening). */
  reedsOpgenomenVorigeWerkgeverUren: number
}

export interface OuderschapsRecht {
  totaalUren: number
  betaaldUren: number
  onbetaaldUren: number
  /** Uiterste opnamedatum van het betaalde deel (einde van die dag). */
  betaaldExpiresAt: Date
  /** Verval van het totale recht: de dag vóór de verjaardag. */
  totaalExpiresAt: Date
}

/** UTC-veilige "plus N maanden" (clamp op maandeinde: 31 jan + 1m → 28/29 feb). */
export function addMaandenUtc(datum: Date, maanden: number): Date {
  const jaar = datum.getUTCFullYear()
  const maand = datum.getUTCMonth() + maanden
  const dag = datum.getUTCDate()
  const result = new Date(Date.UTC(jaar, maand, 1))
  const dagenInDoelMaand = new Date(
    Date.UTC(result.getUTCFullYear(), result.getUTCMonth() + 1, 0),
  ).getUTCDate()
  result.setUTCDate(Math.min(dag, dagenInDoelMaand))
  return result
}

/** De dag vóór de `leeftijd`-de verjaardag (UTC), bv. verval totaalrecht. */
export function dagVoorVerjaardag(geboortedatum: Date, leeftijd: number): Date {
  const verjaardag = new Date(
    Date.UTC(
      geboortedatum.getUTCFullYear() + leeftijd,
      geboortedatum.getUTCMonth(),
      geboortedatum.getUTCDate(),
    ),
  )
  verjaardag.setUTCDate(verjaardag.getUTCDate() - 1)
  return verjaardag
}

/**
 * Bereken het ouderschapsverlof-recht voor één kind op basis van de
 * contracturen per week op de berekendatum. Pro-rata voor parttimers zit in
 * de weken×uren-formule zelf (28u-contract → 728u i.p.v. 988u).
 */
export function berekenOuderschapsRecht(
  kind: OuderschapsKindInput,
  beleid: OuderschapsBeleidInput,
  contractUrenPerWeek: number,
): OuderschapsRecht {
  const brutoBetaald = round2(beleid.betaaldWeken * contractUrenPerWeek)
  const brutoOnbetaald = round2(
    (beleid.totaalWeken - beleid.betaaldWeken) * contractUrenPerWeek,
  )

  // Verrekening vorige werkgever: eerst van het deel dat daar gebruikt is;
  // een overschot loopt door naar het andere deel (totaal klopt altijd).
  const verrekening = Math.max(0, kind.reedsOpgenomenVorigeWerkgeverUren)
  let betaald = brutoBetaald
  let onbetaald = brutoOnbetaald
  if (kind.betaaldDeelGebruikt) {
    const vanBetaald = Math.min(betaald, verrekening)
    betaald = round2(betaald - vanBetaald)
    onbetaald = round2(Math.max(0, onbetaald - (verrekening - vanBetaald)))
  } else {
    const vanOnbetaald = Math.min(onbetaald, verrekening)
    onbetaald = round2(onbetaald - vanOnbetaald)
    betaald = round2(Math.max(0, betaald - (verrekening - vanOnbetaald)))
  }

  return {
    totaalUren: round2(betaald + onbetaald),
    betaaldUren: betaald,
    onbetaaldUren: onbetaald,
    betaaldExpiresAt: addMaandenUtc(kind.geboortedatum, beleid.betaaldBinnenMaandenNaGeboorte),
    totaalExpiresAt: dagVoorVerjaardag(kind.geboortedatum, beleid.kindTotLeeftijd),
  }
}

/**
 * Arbeidsduurwijziging (spec Deel 2b): het RESTERENDE recht wordt herrekend
 * naar de nieuwe weekuren; reeds opgenomen uren blijven historisch staan.
 *
 * Keuze (gedocumenteerd): we rekenen het resterende deel terug naar weken via
 * de contracturen waarmee het recht laatst berekend is
 * (`berekendMetUrenPerWeek`, gesnapshot op de entitlement) en vermenigvuldigen
 * die rest-weken met de nieuwe weekuren:
 *
 *   restWeken  = (hours − usedHours) / berekendMetUrenPerWeek
 *   nieuwHours = usedHours + restWeken × nieuweUrenPerWeek
 *
 * Idempotent: nogmaals draaien met dezelfde nieuwe uren wijzigt niets, omdat
 * de snapshot na herberekening op de nieuwe uren staat.
 */
export function herberekenOuderschapsRestRecht(input: {
  hours: number
  usedHours: number
  berekendMetUrenPerWeek: number
  nieuweUrenPerWeek: number
}): number {
  if (input.berekendMetUrenPerWeek <= 0) return round2(input.hours)
  const restWeken = Math.max(0, (input.hours - input.usedHours) / input.berekendMetUrenPerWeek)
  return round2(input.usedHours + restWeken * input.nieuweUrenPerWeek)
}

/** Is het betaalde deel nog opneembaar op `datum` (t/m de vervaldag zelf)? */
export function betaaldDeelNogOpneembaar(betaaldExpiresAt: Date, datum: Date): boolean {
  const grens = new Date(
    Date.UTC(
      betaaldExpiresAt.getUTCFullYear(),
      betaaldExpiresAt.getUTCMonth(),
      betaaldExpiresAt.getUTCDate(),
      23,
      59,
      59,
      999,
    ),
  )
  return datum.getTime() <= grens.getTime()
}
